/**
 * RecordingPanel
 * Container for call recording functionality in SNPod.
 * When in an iframe (ServiceNow), opens a popup window with a dedicated recorder page.
 * When standalone, uses the in-page modal.
 */

import { useState, useEffect, useCallback } from 'react'
import { Mic, Phone, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useCallRecordingContext } from '@/contexts/CallRecordingContext'
import { CallRecorderModal } from '@/components/calls/CallRecorderModal'
import { FloatingRecorderWidget } from '@/components/calls/FloatingRecorderWidget'
import { CallNotesModal } from '@/components/calls/CallNotesModal'

interface RecordingPanelProps {
  sysId: string | null
  table: string | null
  recordDescription?: string
}

function isInIframe(): boolean {
  try {
    return window.self !== window.top
  } catch {
    return true
  }
}

export function RecordingPanel({ sysId, table, recordDescription }: RecordingPanelProps) {
  const [showRecorderModal, setShowRecorderModal] = useState(false)
  const [showNotesModal, setShowNotesModal] = useState(false)
  const [popupMessage, setPopupMessage] = useState<string | null>(null)

  const { status, isRecording, setRecordingId, setStatus } = useCallRecordingContext()

  const inIframe = isInIframe()

  // Listen for messages from popup recorder window
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'snpod-recording-complete') {
        setPopupMessage('Recording processed successfully')
        if (event.data.recordingId) {
          setRecordingId(event.data.recordingId)
          setStatus('complete')
          setShowNotesModal(true)
        }
      } else if (event.data?.type === 'snpod-recording-cancelled') {
        setPopupMessage(null)
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [setRecordingId, setStatus])

  // Open popup with dedicated recorder page (not full app)
  const openRecorderPopup = useCallback(() => {
    const params = new URLSearchParams({ mode: 'recorder' })
    if (sysId) params.set('sys_id', sysId)
    if (table) params.set('table', table)
    if (document.documentElement.classList.contains('dark')) params.set('dark', '1')

    // Pass auth params so the popup can create its own session
    const parentParams = new URLSearchParams(window.location.search)
    const apiKey = parentParams.get('api_key')
    const userId = parentParams.get('user_id')
    const userName = parentParams.get('user_name')
    if (apiKey) params.set('api_key', apiKey)
    if (userId) params.set('user_id', userId)
    if (userName) params.set('user_name', userName)

    const width = 420
    const height = 580
    const left = window.screenX + (window.outerWidth - width) / 2
    const top = window.screenY + (window.outerHeight - height) / 2

    const popup = window.open(
      `${window.location.origin}/recorder?${params.toString()}`,
      'snpod-recorder',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    )

    if (!popup) {
      setPopupMessage('Popup blocked. Please allow popups for this site and try again.')
    }
  }, [sysId, table])

  const handleRecordClick = () => {
    if (inIframe) {
      openRecorderPopup()
    } else {
      setShowRecorderModal(true)
    }
  }

  const handleProcessingComplete = () => {
    setShowNotesModal(true)
  }

  const isActive = isRecording || status === 'recording' || status === 'uploading' || status === 'processing'

  return (
    <div className="flex flex-col h-full">
      {/* Active recording - compact widget */}
      {isActive ? (
        <div className="flex items-center gap-3 p-3 m-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <span className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-red-800 dark:text-red-300">Recording in progress</p>
            {sysId && <p className="text-[10px] text-red-600 dark:text-red-400 truncate">Record: {sysId}</p>}
          </div>
          <Phone className="h-4 w-4 text-red-500 shrink-0" />
        </div>
      ) : (
        /* Idle / Complete state */
        <div className="flex flex-col items-center justify-center flex-1 gap-3 p-4">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-50 dark:bg-blue-900/30">
            <Phone className="h-6 w-6 text-blue-500" />
          </div>

          <div className="text-center">
            <h3 className="text-sm font-medium text-gray-800 dark:text-slate-200">Call Recording</h3>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Record, transcribe, and add notes to tickets
            </p>
          </div>

          {status !== 'complete' && (
            <Button
              size="sm"
              onClick={handleRecordClick}
              className="gap-1.5 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {inIframe ? <ExternalLink className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
              Record Call
            </Button>
          )}

          {inIframe && status !== 'complete' && (
            <p className="text-[9px] text-muted-foreground text-center max-w-[180px]">
              Opens in a new window (mic requires standalone page)
            </p>
          )}

          {status === 'complete' && !showNotesModal && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowNotesModal(true)}
              className="gap-1.5"
            >
              <Mic className="h-3.5 w-3.5" />
              View Notes
            </Button>
          )}

          {popupMessage && (
            <p className="text-[10px] text-green-600 text-center">{popupMessage}</p>
          )}
        </div>
      )}

      {/* In-page flow (only when NOT in iframe) */}
      {!inIframe && (
        <>
          <CallRecorderModal
            isOpen={showRecorderModal}
            onClose={() => setShowRecorderModal(false)}
            sysId={sysId ?? undefined}
            table={table ?? undefined}
            recordDescription={recordDescription}
            onRecordingStart={() => {}}
          />
          <FloatingRecorderWidget
            onProcessingComplete={handleProcessingComplete}
          />
        </>
      )}

      {/* Notes modal */}
      <CallNotesModal
        isOpen={showNotesModal}
        onClose={() => setShowNotesModal(false)}
        sysId={sysId}
        table={table}
      />
    </div>
  )
}
